import get from 'lodash.get';
import { Component } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { IntlContext, FormattedMessage, defineMessages } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { validate } from 'dibs-address-validator/exports/validate';
import { Fields } from '../../utils/constants';
import styles from '../../styles/main.scss';

// components
import FirstName from '../fields/FirstName';
import LastName from '../fields/LastName';
import Company from '../fields/Company';
import Country from '../fields/Country';
import Line1 from '../fields/Line1';
import Line2 from '../fields/Line2';
import City from '../fields/City';
import State from '../fields/State';
import StateUS from '../fields/StateUS';
import ZipCode from '../fields/ZipCode';
import CityState from '../fields/CityState';
import PhoneNumber from '../fields/PhoneNumber';
import Submit from '../inputs/Submit';

const messages = defineMessages({
    otherCityState: {
        id: 'dc.AddressForm.otherCityState',
        defaultMessage: 'Other city and state',
    },
});

class AddressForm extends Component {
    constructor(props) {
        super(props);
        const {
            address: _address,
            fields: _fields,
            disabled,
            showCityAndState,
            hideCompany,
            hidePhoneNumber,
        } = props;
        const address = { ..._address, country: _address.country || 'US' };
        const { country, city, state, zipCode = '' } = address;
        const fields = _fields.filter(
            f => !((f === 'company' && hideCompany) || (f === 'phoneNumber' && hidePhoneNumber))
        );

        this.onZipCodeDropDown = this.onZipCodeDropDown.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        const componentState = {
            address: fields.reduce((o, f) => ({ ...o, [f]: address[f] || '' }), {}),
            blurred: fields.reduce((o, f) => ({ ...o, [f]: false }), {}),
            disabled: fields.reduce((o, f) => ({ ...o, [f]: disabled.includes(f) }), {}),
            showCityAndState:
                showCityAndState ||
                !!disabled.zipCode ||
                country !== 'US' ||
                ((zipCode || '').length >= 5 && !(city && state)),
            isSaving: false,
            phoneNumber: '',
        };

        this.state = {
            ...componentState,
            ...this.getAddressWithCityAndState(componentState, {}, props),
        };
    }
    componentDidMount() {
        this.props.onMount();
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { submitTriggered: prevSubmitTriggered } = this.props;
        const { submitTriggered, hideSubmit } = nextProps;
        const { address, showCityAndState } = this.getAddressWithCityAndState(
            this.state,
            this.props,
            nextProps
        );

        this.setState({
            showCityAndState: showCityAndState || this.state.showCityAndState,
            address,
        });

        if (nextProps.validateForm || (hideSubmit && !prevSubmitTriggered && submitTriggered)) {
            this.onSubmit();
        }
    }
    getAddressWithCityAndState(componentState, prevProps, nextProps) {
        const { onUpdate } = nextProps;
        const { address, showCityAndState } = componentState;
        const prevZipCodeLookUp = get(prevProps, 'viewer.zipCodeLookUp');
        const nextZipCodeLookUp = get(nextProps, 'viewer.zipCodeLookUp');
        const noResultFound = Array.isArray(nextZipCodeLookUp) && !nextZipCodeLookUp.length;

        const prevCity = get(prevZipCodeLookUp, '[0].city');
        const prevState = get(prevZipCodeLookUp, '[0].state');
        const city = get(nextZipCodeLookUp, '[0].city') || '';
        const state = get(nextZipCodeLookUp, '[0].state') || '';

        const shouldUpdate = !!(city && state) && (city !== prevCity || state !== prevState);
        const newAddress = { ...address, city, state };

        if (shouldUpdate && onUpdate) {
            onUpdate({ address: { ...address, city }, field: 'city', value: city });
            onUpdate({ address: newAddress, field: 'state', value: state });
        }

        return {
            showCityAndState: address.country !== 'US' || showCityAndState || noResultFound,
            address: shouldUpdate ? newAddress : address,
        };
    }
    onZipCodeDropDown({ value }) {
        this.setState({
            blurred: { ...this.state.blurred, city: true, state: true },
            showCityAndState: value === 'otherCityState',
        });
    }
    onValidate() {
        const { address } = this.state;
        const { fields, validations, hidePhoneNumber } = this.props;
        return validate(address, {
            fields,
            isPhoneRequired: !hidePhoneNumber,
            ...validations,
        });
    }
    onChange({ name, value, isValid }, cb = () => {}) {
        const { blurred } = this.state;
        const { relay, onUpdate } = this.props;
        const address = {
            ...this.state.address,
            [name]: name === 'phoneNumber' && !isValid ? '' : value,
        };
        const { country, zipCode = '' } = address;
        const shouldFetchGeography =
            name === 'zipCode' &&
            country === 'US' &&
            zipCode.length >= 5 &&
            zipCode.substring(0, 5) !== (this.state.address.zipCode || '').substring(0, 5);

        if (shouldFetchGeography) {
            relay.refetch({
                country,
                zipCode: zipCode.substring(0, 5),
                canCheckZipCode: true,
            });
        }
        if (onUpdate) {
            onUpdate({ address, field: name, value });
        }
        this.setState(
            {
                address,
                blurred: { ...blurred, [name]: false },
            },
            () => cb({ name, value })
        );
    }
    onBlur({ name, value = '' }) {
        const { address } = this.state;
        const { onBlur } = this.props;
        const blurred = Object.keys(address).reduce((obj, field) => {
            obj[field] = !!address[field];
            return obj;
        }, {});
        if (onBlur) {
            const { errors = {} } = this.onValidate();
            const error = !value.trim()
                ? null
                : get(errors[name], 'defaultMessage') || errors[name] || null;
            onBlur({ field: name, value, error });
        }
        this.setState({ blurred });
    }
    onSubmit() {
        const { address } = this.state;
        const { hideSubmit, disableSubmit, onSubmit } = this.props;
        const { isValid, errors } = this.onValidate();

        // ensure trimmed whitespace values for city and state
        const trimmedValuesAddress = {
            ...address,
            city: address.city.trim(),
            state: address.state.trim(),
        };

        onSubmit({ address: trimmedValuesAddress, isValid, errors });

        if (!disableSubmit && isValid && !hideSubmit) {
            this.setState({ isSaving: true });
        } else {
            this.setState({
                blurred: Object.keys(address).reduce(
                    (obj, field) => ({ ...obj, [field]: true }),
                    {}
                ),
            });
        }
    }
    render() {
        const { address, blurred, disabled, showCityAndState, isSaving } = this.state;
        const {
            viewer: { geography },
            children,
            wrapperClass,
            formClass,
            theme,
            fieldSize,
            autoFocus,
            submitLabel,
            hideSubmit,
            disableForm,
            disableSubmit,
            renderChildren,
            showAllErrors,
            relay,
        } = this.props;
        const { errors, isValid } = this.onValidate();

        const wrapperClasses = classNames(wrapperClass, theme.wrapper);
        const formClasses = classNames(formClass, theme.form);
        const labelClasses = classNames(styles.right, styles.textWrapper);
        const isCountryUS = address.country === 'US';
        const isCityStateKnown = !!(address.city && address.state);
        const countries = (geography?.countries || []).map(country => ({
            label: country.name,
            value: country.abbreviation,
        }));
        const usStates = (geography?.usStates || []).map(state => ({
            label: state.name,
            value: state.abbreviation,
        }));
        const cityStateOptions = [
            { value: 'cityState', label: `${address.city}, ${address.state}` },
            { value: 'otherCityState', label: this.context.formatMessage(messages.otherCityState) },
        ];

        const props = Object.keys(address).reduce(
            (obj, field) => ({
                ...obj,
                [field]: {
                    fieldSize,
                    value: address[field],
                    error: errors[field] ? this.context.formatMessage(errors[field]) : null,
                    isBlurred: showAllErrors || blurred[field],
                    disabled: isSaving || disableForm || disabled[field],
                    onChange: this.onChange,
                    onBlur: this.onBlur,
                },
            }),
            {}
        );

        const showName = !!(props.firstName && props.lastName);
        const showCompany = !!props.company;
        const showCountry = !!props.country;
        const showLine1 = !!props.line1;
        const showLine2 = !!props.line2;
        const showZipCode = !!props.zipCode;
        const showCity = !isCountryUS || showCityAndState || !!disabled.city;
        const showState = !isCountryUS;
        const showStateUS = isCountryUS && (showCityAndState || !!disabled.state);
        const showCityStateDropDown =
            isCountryUS && !showCityAndState && isCityStateKnown && !disabled.zipCode;
        const showCityStateLabel = isCountryUS && !showCityStateDropDown && !isCityStateKnown;
        const showPhoneNumber = !!props.phoneNumber;

        return (
            <div className={wrapperClasses} data-tn="dibs-address-form-wrapper">
                <form className={formClasses}>
                    {showName && (
                        <div className={styles.group} data-field="displayName">
                            <FirstName {...props.firstName} autoFocus={autoFocus} />
                            <LastName {...props.lastName} />
                        </div>
                    )}

                    {showCompany && <Company {...props.company} />}

                    {showCountry && <Country {...props.country} options={countries} />}
                    {showLine1 && <Line1 {...props.line1} />}
                    {showLine2 && <Line2 {...props.line2} />}

                    {showCity && <City {...props.city} />}
                    {showState && <State {...props.state} />}
                    {showStateUS && <StateUS {...props.state} options={usStates} />}

                    {showZipCode && (
                        <div className={styles.group} data-field="zipCode">
                            <ZipCode {...props.zipCode} isCountryUS={isCountryUS} />
                            {showCityStateDropDown && (
                                <CityState
                                    value="cityState"
                                    city={address.city}
                                    state={address.state}
                                    options={cityStateOptions}
                                    fieldSize={fieldSize}
                                    isBlurred={blurred.zipCode}
                                    disabled={isSaving || disableForm}
                                    onChange={this.onZipCodeDropDown}
                                />
                            )}
                            {showCityStateLabel && (
                                <div className={labelClasses}>
                                    <span className={styles.text}>
                                        <FormattedMessage
                                            id="dc.AddressForm.zipCode"
                                            defaultMessage="Enter ZIP code for City and State"
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {showPhoneNumber && (
                        <div className={styles.group} data-field="phoneNumber">
                            <PhoneNumber
                                {...props.phoneNumber}
                                countryCode={props?.country?.value}
                                environment={relay.environment}
                                viewer={this.props.viewer}
                            />
                            <div className={labelClasses}>
                                <span className={styles.text}>
                                    <FormattedMessage
                                        id="dc.AddressForm.shippingQuestions"
                                        defaultMessage="For shipping questions only"
                                    />
                                </span>
                            </div>
                        </div>
                    )}
                    {children}
                    {renderChildren({ address, errors, isValid })}
                </form>
                {!hideSubmit && (
                    <Submit
                        label={submitLabel}
                        disabled={disableSubmit || isSaving || !isValid}
                        wrapperClass={theme.submit}
                        isSaving={isSaving}
                        onClick={this.onSubmit}
                    />
                )}
            </div>
        );
    }
}

AddressForm.contextType = IntlContext;

AddressForm.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    wrapperClass: PropTypes.string,
    formClass: PropTypes.string,
    theme: PropTypes.object,
    address: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.string),
    fieldSize: PropTypes.oneOf(['small', 'medium', 'large']),
    disabled: PropTypes.array,
    validations: PropTypes.object,
    autoFocus: PropTypes.bool,
    showCityAndState: PropTypes.bool,
    hideCompany: PropTypes.bool,
    hidePhoneNumber: PropTypes.bool,
    hideSubmit: PropTypes.bool,
    submitLabel: PropTypes.node,
    submitTriggered: PropTypes.bool,
    disableForm: PropTypes.bool,
    disableSubmit: PropTypes.bool,
    validateForm: PropTypes.bool,
    children: PropTypes.any,
    renderChildren: PropTypes.func,
    onMount: PropTypes.func,
    onUpdate: PropTypes.func,
    onBlur: PropTypes.func,
    onSubmit: PropTypes.func,
    showAllErrors: PropTypes.bool,
};

AddressForm.defaultProps = {
    wrapperClass: '',
    formClass: '',
    theme: {},
    address: {},
    fields: Fields,
    fieldSize: null,
    disabled: [],
    validations: {},
    autoFocus: false,
    showCityAndState: false,
    hideCompany: false,
    hidePhoneNumber: false,
    disableForm: false,
    disableSubmit: false,
    validateForm: false,
    renderChildren: () => null,
    onMount: () => null,
    onSubmit: () => null,
    showAllErrors: false,
};

// reassinging class to refetch container allows the graphql
// query to use AddressForm.getFragment for classic mode
AddressForm = createRefetchContainer(
    AddressForm,
    {
        viewer: graphql`
            fragment AddressForm_viewer on Viewer {
                geography {
                    countries {
                        name
                        abbreviation
                    }
                    usStates {
                        name
                        abbreviation
                    }
                }
                zipCodeLookUp: zipCode(zipCode: $zipCode, country: $country)
                    @include(if: $canCheckZipCode) {
                    city
                    state
                }
            }
        `,
    },
    graphql`
        query AddressFormRefetchQuery(
            $zipCode: String = ""
            $country: String = ""
            $canCheckZipCode: Boolean!
        ) {
            viewer {
                ...AddressForm_viewer
            }
        }
    `
);

export default AddressForm;
